import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import { RevealWrapper } from '../hooks/useReveal';
import { Building2, Mail, Phone, Globe, MapPin, User, FileText, Scale, ExternalLink } from 'lucide-react';
import { Separator } from '../components/ui/separator';

export const ImpressumPage = () => {
  return (
    <div className="py-16 md:py-24" data-testid="impressum-page">
      <div className="container mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
        <RevealWrapper>
          <div className="mb-8 text-center">
            <h1 className="font-display text-4xl font-semibold mb-4">Impressum</h1>
            <p className="text-muted-foreground">
              Angaben gemäß § 5 TMG
            </p>
          </div>
        </RevealWrapper>

        <RevealWrapper delay={100}>
          <Card className="glass-card border-primary/20">
            <CardContent className="p-8 space-y-8">
              {/* Firmenname & Hauptsitz + Held */}
              <div className="flex items-stretch gap-6">
                <div className="flex-1 rounded-lg bg-gradient-to-r from-primary/10 to-blue-500/10 p-6 border border-primary/20">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/20">
                      <Building2 className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <h2 className="text-xl font-semibold">Host-On Service Provider GmbH</h2>
                      <p className="text-sm text-muted-foreground">Betreiber von Wolkenhelden</p>
                    </div>
                  </div>
                  
                  <div className="grid gap-4 sm:grid-cols-2 mt-6">
                    <div className="flex items-start gap-3">
                      <MapPin className="h-5 w-5 text-primary mt-0.5" />
                      <div>
                        <p className="text-sm text-muted-foreground">Hauptsitz</p>
                        <p className="font-medium">Hanauer Landstraße 204</p>
                        <p className="font-medium">60314 Frankfurt am Main</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <Phone className="h-5 w-5 text-primary mt-0.5" />
                      <div>
                        <p className="text-sm text-muted-foreground">Telefon</p>
                        <a href="tel:+496924749900" className="font-medium hover:text-primary">
                          +49 (0) 69 247 499 - 0
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="hidden sm:flex items-end flex-shrink-0">
                  <img 
                    src="/images/held_impressum.png" 
                    alt="Wolkenheld mit Host-On Logo" 
                    className="h-48 w-auto block dark:hidden"
                  />
                  <img 
                    src="/images/held_impressum_dark.png" 
                    alt="Wolkenheld mit Host-On Logo" 
                    className="h-48 w-auto hidden dark:block"
                  />
                </div>
              </div>

              {/* Zweigstelle */}
              <div>
                <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
                  <MapPin className="h-5 w-5 text-primary" />
                  Zweigstelle Niedersachsen
                </h2>
                <div className="bg-muted/30 rounded-lg p-4">
                  <p className="font-medium">Host-On Service Provider GmbH</p>
                  <p className="text-muted-foreground">Birkenallee 25</p>
                  <p className="text-muted-foreground">29525 Uelzen</p>
                  <p className="text-muted-foreground mt-2">
                    Telefon: <a href="tel:+4958124199910" className="hover:text-primary">+49 (0) 581 241 999 10</a>
                  </p>
                </div>
              </div>

              {/* Geschäftsführung */}
              <div>
                <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
                  <User className="h-5 w-5 text-primary" />
                  Geschäftsführung
                </h2>
                <div className="grid gap-3 sm:grid-cols-3">
                  <div className="p-3 rounded-lg bg-muted/30">
                    <p className="font-medium">Gabriele Gohlke</p>
                    <p className="text-sm text-muted-foreground">Geschäftsführerin (CEO)</p>
                  </div>
                  <div className="p-3 rounded-lg bg-muted/30">
                    <p className="font-medium">Philip Grassegger</p>
                    <p className="text-sm text-muted-foreground">Technische Leitung (CTO)</p>
                  </div>
                  <div className="p-3 rounded-lg bg-muted/30">
                    <p className="font-medium">Kay Wille</p>
                    <p className="text-sm text-muted-foreground">Leiter Customer Support</p>
                  </div>
                </div>
              </div>

              {/* Registereintrag */}
              <div>
                <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
                  <FileText className="h-5 w-5 text-primary" />
                  Registereintrag
                </h2>
                <div className="bg-muted/30 rounded-lg p-4 space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground">Registergericht:</span>
                    <span className="font-medium">Amtsgericht Frankfurt am Main</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground">Registernummer:</span>
                    <span className="font-medium font-mono">HRB 134674</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground">USt-IdNr. (§ 27 a UStG):</span>
                    <span className="font-medium font-mono">DE359953533</span>
                  </div>
                </div>
              </div>

              {/* Kontakt */}
              <div>
                <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
                  <Mail className="h-5 w-5 text-primary" />
                  Kontakt
                </h2>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="flex items-center gap-3 p-4 rounded-lg bg-muted/30">
                    <Mail className="h-5 w-5 text-primary" />
                    <div>
                      <p className="text-sm text-muted-foreground">E-Mail (Wolkenhelden)</p>
                      <a href="mailto:kontakt@wolkenhelden.de" className="font-medium hover:text-primary">
                        kontakt@wolkenhelden.de
                      </a>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-4 rounded-lg bg-muted/30">
                    <Globe className="h-5 w-5 text-primary" />
                    <div>
                      <p className="text-sm text-muted-foreground">Website</p>
                      <a href="https://wolkenhelden.de" target="_blank" rel="noopener noreferrer" className="font-medium hover:text-primary">
                        wolkenhelden.de
                      </a>
                    </div>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mt-3 italic">
                  Hinweis: Für Support-Anfragen nutzen Sie bitte support@wolkenhelden.de
                </p>
              </div>

              <Separator />

              {/* Haftungsausschluss */}
              <div>
                <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
                  <Scale className="h-5 w-5 text-primary" />
                  Haftungsausschluss
                </h2>
                <div className="space-y-4 text-sm text-muted-foreground">
                  <div>
                    <h3 className="font-medium text-foreground mb-1">Haftung für Inhalte</h3>
                    <p>
                      Die Inhalte unserer Seiten wurden mit größter Sorgfalt erstellt. Für die Richtigkeit, 
                      Vollständigkeit und Aktualität der Inhalte können wir jedoch keine Gewähr übernehmen. 
                      Als Diensteanbieter sind wir gemäß § 7 Abs.1 TMG für eigene Inhalte auf diesen Seiten 
                      nach den allgemeinen Gesetzen verantwortlich.
                    </p>
                  </div>
                  <div>
                    <h3 className="font-medium text-foreground mb-1">Haftung für Links</h3>
                    <p>
                      Unser Angebot enthält Links zu externen Webseiten Dritter, auf deren Inhalte wir keinen 
                      Einfluss haben. Deshalb können wir für diese fremden Inhalte auch keine Gewähr übernehmen. 
                      Für die Inhalte der verlinkten Seiten ist stets der jeweilige Anbieter oder Betreiber der 
                      Seiten verantwortlich.
                    </p>
                  </div>
                  <div>
                    <h3 className="font-medium text-foreground mb-1">Urheberrecht</h3>
                    <p>
                      Die durch die Seitenbetreiber erstellten Inhalte und Werke auf diesen Seiten unterliegen 
                      dem deutschen Urheberrecht. Die Vervielfältigung, Bearbeitung, Verbreitung und jede Art 
                      der Verwertung außerhalb der Grenzen des Urheberrechtes bedürfen der schriftlichen 
                      Zustimmung des jeweiligen Autors bzw. Erstellers.
                    </p>
                  </div>
                </div>
              </div>

              {/* EU-Streitschlichtung */}
              <div>
                <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
                  🇪🇺 EU-Streitschlichtung
                </h2>
                <p className="text-muted-foreground text-sm">
                  Die Europäische Kommission stellt eine Plattform zur Online-Streitbeilegung (OS) bereit:
                </p>
                <a 
                  href="https://ec.europa.eu/consumers/odr" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="text-primary hover:underline inline-flex items-center gap-1 mt-2"
                >
                  https://ec.europa.eu/consumers/odr
                  <ExternalLink className="h-3 w-3" />
                </a>
              </div>

              {/* Werbung */}
              <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-4">
                <p className="text-sm text-amber-700 dark:text-amber-400">
                  <strong>Hinweis:</strong> Die Verwendung der Kontaktdaten des Impressums zur Versendung 
                  gewerblicher Werbung ist ausdrücklich nicht erwünscht, es sei denn wir haben zuvor eine 
                  schriftliche Einwilligung erteilt oder es besteht bereits eine Geschäftsbeziehung.
                </p>
              </div>

              {/* Stand */}
              <p className="text-xs text-muted-foreground text-center">
                Stand: 02.05.2024
              </p>
            </CardContent>
          </Card>
        </RevealWrapper>
      </div>
    </div>
  );
};
